export interface StorePreferenceOption {
  id: string;
  name: string;
  popular: boolean;
}

export const STORE_PREFERENCE_OPTIONS: StorePreferenceOption[] = [
  { id: "walmart", name: "Walmart", popular: true },
  { id: "aldi", name: "ALDI", popular: true },
  { id: "kroger", name: "Kroger", popular: false },
  { id: "marianos", name: "Mariano's", popular: false },
  { id: "wegmans", name: "Wegmans", popular: false },
  { id: "whole_foods", name: "Whole Foods", popular: true },
  { id: "costco", name: "Costco", popular: false },
  { id: "trader_joes", name: "Trader Joe's", popular: false },
  { id: "sams_club", name: "Sam's Club", popular: false },
  { id: "safeway", name: "Safeway", popular: false },
  { id: "albertsons", name: "Albertsons", popular: false },
  { id: "publix", name: "Publix", popular: false },
  { id: "heb", name: "H-E-B", popular: false },
  { id: "jewel_osco", name: "Jewel-Osco", popular: false },
  { id: "ahold_delhaize", name: "Ahold Delhaize", popular: false },
  { id: "meijer", name: "Meijer", popular: false },
  { id: "winco", name: "WinCo Foods", popular: false },
  { id: "bjs", name: "BJ's Wholesale Club", popular: false },
  { id: "dollar_general", name: "Dollar General", popular: false },
  { id: "dollar_tree", name: "Dollar Tree", popular: false },
  { id: "hy_vee", name: "Hy-Vee", popular: false },
  { id: "sprouts", name: "Sprouts Farmers Market", popular: false },
  { id: "giant_eagle", name: "Giant Eagle", popular: false },
  { id: "price_chopper", name: "Price Chopper", popular: false },
  { id: "cash_saver", name: "Cash Saver", popular: false },
  { id: "south_point_grocery", name: "South Point Grocery", popular: false },
  { id: "high_point_grocery", name: "High Point Grocery", popular: false },
  { id: "rs_market", name: "RS Market", popular: false },
  { id: "miss_cordelias", name: "Miss Cordelia's", popular: false },
];

export const STORE_PREFERENCE_ORDER = STORE_PREFERENCE_OPTIONS.map((store) => store.id);
export const DEFAULT_STORE_PREFERENCE_IDS = ["walmart", "aldi", "whole_foods"] as const;
export const ACTIVE_SEARCH_STORES_STORAGE_KEY = "active-search-stores";
export const STORE_PREFERENCE_POPULAR_IDS = STORE_PREFERENCE_OPTIONS
  .filter((store) => store.popular)
  .map((store) => store.id);

const STORE_PREFERENCE_ORDER_INDEX = new Map(
  STORE_PREFERENCE_ORDER.map((storeId, index) => [storeId, index]),
);
const STORE_PREFERENCE_ID_SET = new Set(STORE_PREFERENCE_ORDER);

export const normalizeStorePreferenceId = (value: string): string => value.trim().toLowerCase();

export const isSupportedStorePreferenceId = (value: string): boolean =>
  STORE_PREFERENCE_ID_SET.has(normalizeStorePreferenceId(value));

export const sanitizeStorePreferenceIds = (
  values?: readonly string[] | null,
): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set<string>();
  const sanitized: string[] = [];

  values.forEach((value) => {
    if (typeof value !== "string") {
      return;
    }

    const normalized = normalizeStorePreferenceId(value);
    if (!normalized || !STORE_PREFERENCE_ID_SET.has(normalized) || seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    sanitized.push(normalized);
  });

  return sanitized;
};

export const sortStorePreferenceIds = (values: readonly string[]): string[] => {
  return [...sanitizeStorePreferenceIds(values)].sort((a, b) => {
    const indexA = STORE_PREFERENCE_ORDER_INDEX.get(a);
    const indexB = STORE_PREFERENCE_ORDER_INDEX.get(b);

    if (typeof indexA === "number" && typeof indexB === "number") {
      return indexA - indexB;
    }
    if (typeof indexA === "number") return -1;
    if (typeof indexB === "number") return 1;
    return a.localeCompare(b);
  });
};

export const areStorePreferenceListsEqual = (
  a?: readonly string[] | null,
  b?: readonly string[] | null,
): boolean => {
  const sortedA = sortStorePreferenceIds(a || []);
  const sortedB = sortStorePreferenceIds(b || []);
  return JSON.stringify(sortedA) === JSON.stringify(sortedB);
};

export const getDefaultStorePreferenceSelection = (
  preferredStoreIds?: readonly string[] | null,
): string[] => {
  const sanitized = sanitizeStorePreferenceIds(preferredStoreIds);
  if (sanitized.length > 0) {
    return sanitized;
  }
  return [...DEFAULT_STORE_PREFERENCE_IDS];
};

export const readStoredSearchStores = (): string[] => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(ACTIVE_SEARCH_STORES_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    return sanitizeStorePreferenceIds(raw.split(","));
  } catch {
    return [];
  }
};

export const persistSearchStores = (storeIds?: readonly string[] | null): void => {
  if (typeof window === "undefined") {
    return;
  }

  const sanitized = sanitizeStorePreferenceIds(storeIds || []);

  try {
    if (sanitized.length > 0) {
      window.localStorage.setItem(ACTIVE_SEARCH_STORES_STORAGE_KEY, sanitized.join(","));
    } else {
      window.localStorage.removeItem(ACTIVE_SEARCH_STORES_STORAGE_KEY);
    }
  } catch {
    // Ignore localStorage failures.
  }
};
